// Blog content store (John's own articles).
// To publish a new article: add an entry to `articles` below. Set `featured: true`
// to surface it at the top of /blog. Set `trending: true` to badge it.
// The newest item (by `date`) becomes the hero on the index page automatically.

import njLuxuryHome from "@/assets/nj-luxury-home.webp";
import njLuxuryInterior from "@/assets/nj-luxury-interior.jpg";
import njLuxuryHero from "@/assets/nj-luxury-hero.jpg";
import njMapBg from "@/assets/nj-map-bg.webp";
import princetonImg from "@/assets/nj-communities/princeton.jpg";
import oceanCountyImg from "@/assets/nj-communities/ocean-county.webp";

export type InsightCategory =
  | "Market Update"
  | "Buyer Insights"
  | "Seller Insights"
  | "Mortgage & Rates"
  | "Inventory & Pricing"
  | "Local Spotlight";

export interface Insight {
  slug: string;
  title: string;
  excerpt: string;
  category: InsightCategory;
  date: string; // ISO
  readMinutes: number;
  /** Optional featured image (imported asset URL). */
  image?: string;
  featured?: boolean;
  trending?: boolean;
  /** Optional pull-quote rendered prominently in the article body. */
  pullQuote?: string;
  /** Article body as ordered blocks. Keep paragraphs short and scannable. */
  body: Array<
    | { type: "p"; text: string }
    | { type: "h2"; text: string }
    | { type: "h3"; text: string }
    | { type: "ul"; items: string[] }
    | { type: "stat"; label: string; value: string; note?: string }
    | { type: "quote"; text: string; attribution?: string }
  >;
}

export const marketSnapshot = {
  asOf: "November 2026",
  region: "Central & Shore New Jersey",
  metrics: [
    { label: "Median Sale Price", value: "$612K", delta: "+4.8% YoY", trend: "up" as const },
    { label: "Months of Supply", value: "2.4", delta: "Seller's market", trend: "flat" as const },
    { label: "Avg. Days on Market", value: "23", delta: "−6 vs. last yr", trend: "down" as const },
    { label: "30-Yr Fixed Rate", value: "6.18%", delta: "−0.34% QoQ", trend: "down" as const },
  ],
};

// 12-month price trend (median sale price, $K) — update monthly.
export const priceTrend = [
  { month: "Dec", price: 578 },
  { month: "Jan", price: 582 },
  { month: "Feb", price: 588 },
  { month: "Mar", price: 595 },
  { month: "Apr", price: 601 },
  { month: "May", price: 604 },
  { month: "Jun", price: 607 },
  { month: "Jul", price: 605 },
  { month: "Aug", price: 603 },
  { month: "Sep", price: 608 },
  { month: "Oct", price: 610 },
  { month: "Nov", price: 612 },
];

// 12-month mortgage rate trend (30-yr fixed, %).
export const rateTrend = [
  { month: "Dec", rate: 7.12 },
  { month: "Jan", rate: 7.04 },
  { month: "Feb", rate: 6.92 },
  { month: "Mar", rate: 6.88 },
  { month: "Apr", rate: 6.75 },
  { month: "May", rate: 6.71 },
  { month: "Jun", rate: 6.62 },
  { month: "Jul", rate: 6.54 },
  { month: "Aug", rate: 6.47 },
  { month: "Sep", rate: 6.39 },
  { month: "Oct", rate: 6.28 },
  { month: "Nov", rate: 6.18 },
];

// Active inventory by county (most recent month).
export const inventoryByCounty = [
  { county: "Mercer", listings: 412 },
  { county: "Middlesex", listings: 638 },
  { county: "Monmouth", listings: 894 },
  { county: "Ocean", listings: 1024 },
];

export const articles: Insight[] = [
  {
    slug: "central-nj-housing-market-outlook",
    image: njMapBg,
    title: "Central New Jersey Housing Outlook: What to Expect This Season",
    excerpt:
      "Inventory is loosening, rates have softened, and well-priced homes are still moving in under a month. Here's what buyers and sellers should plan for.",
    category: "Market Update",
    date: "2026-11-12",
    readMinutes: 6,
    featured: true,
    trending: true,
    pullQuote:
      "Homes that are priced with discipline and prepared with intention are still the ones that win — even as the market normalizes.",
    body: [
      { type: "p", text: "The Central New Jersey market is entering a more balanced phase. After several years of unprecedented competition, we're seeing inventory grow gradually, days on market lengthen slightly, and buyers regain a degree of negotiating leverage — without prices retreating in any meaningful way." },
      { type: "h2", text: "Where the numbers stand" },
      { type: "stat", label: "Median Sale Price", value: "$612K", note: "+4.8% year-over-year across Mercer, Middlesex, Monmouth and Ocean counties." },
      { type: "p", text: "Median sale price has continued a steady, single-digit climb. The pace is healthier than the double-digit spikes of recent years and suggests durable demand rather than speculative pressure." },
      { type: "h2", text: "What this means if you're buying" },
      { type: "ul", items: [
        "You have more room to write thoughtful offers — fewer waived inspections, more realistic contingencies.",
        "Rate buy-downs and seller concessions are reappearing, especially on homes that have been listed for 30+ days.",
        "Pre-approval is still non-negotiable. Strong listings move quickly even in a calmer market.",
      ]},
      { type: "h2", text: "What this means if you're selling" },
      { type: "p", text: "Pricing strategy matters more than it has in years. Aspirational pricing — even by $10–15K — is now meaningfully slowing time on market and inviting low offers. The sellers winning today are the ones who price with discipline and present their property professionally from day one." },
      { type: "quote", text: "I wouldn't sell you a house I wouldn't sell to my kids — and I wouldn't list your home in a way I wouldn't accept for my own.", attribution: "John Mastroianni" },
      { type: "h2", text: "The bottom line" },
      { type: "p", text: "Central NJ remains a seller's market, but a more rational one. If you've been waiting on the sidelines — as a buyer or a seller — the window for thoughtful, well-prepared moves is genuinely open." },
    ],
  },
  {
    slug: "mortgage-rates-what-buyers-should-know",
    image: njLuxuryHome,
    title: "Mortgage Rates Are Softening: What Buyers Should Actually Do",
    excerpt:
      "Rates have come down meaningfully from their peak. Here's how to think about timing, locking, and the real monthly payment math.",
    category: "Mortgage & Rates",
    date: "2026-11-04",
    readMinutes: 5,
    trending: true,
    body: [
      { type: "p", text: "The 30-year fixed rate has eased to roughly 6.18%, down from highs near 7.5% just a year ago. That shift changes the affordability picture in ways many buyers underestimate." },
      { type: "h2", text: "The payment math, in plain English" },
      { type: "p", text: "On a $500,000 loan, every 0.25% drop in rate trims roughly $80 off the monthly payment. The move from 7.0% to 6.18% saves about $270/month — or more than $97,000 over the life of the loan." },
      { type: "h2", text: "Should you wait for rates to drop further?" },
      { type: "ul", items: [
        "Trying to time the exact bottom rarely works — and inventory typically tightens when rates fall, pushing prices up.",
        "Buying now and refinancing later is a real strategy. Many NJ lenders are offering reduced-cost refis if rates drop another 50 bps.",
        "Focus on the home and the monthly payment you can comfortably live with today, not the headline rate.",
      ]},
      { type: "h2", text: "What I tell my clients" },
      { type: "p", text: "Get pre-approved with two lenders. Lock when you're under contract, not before. And run the math on a 7/1 ARM if you're confident you won't be in the home more than seven years — the savings can be meaningful." },
    ],
  },
  {
    slug: "pricing-your-home-in-a-normalizing-market",
    image: njLuxuryHero,
    title: "Pricing Your Home in a Normalizing Market",
    excerpt:
      "Overpricing is the single most expensive mistake a seller can make right now. Here's the pricing framework I use with every listing.",
    category: "Seller Insights",
    date: "2026-10-28",
    readMinutes: 7,
    featured: true,
    body: [
      { type: "p", text: "When the market is white-hot, you can overprice and still sell. When the market normalizes, you can't. Today's buyers have more options, more time, and access to every comparable sale within a five-mile radius." },
      { type: "h2", text: "The three-window framework" },
      { type: "p", text: "Every listing has three pricing windows — and each one tells a different story to the market." },
      { type: "h3", text: "Window 1: Days 1–10" },
      { type: "p", text: "This is your peak attention window. 70% of qualified buyers in your price band will see your listing in the first ten days. Price for this window — not for the imaginary buyer who shows up in week six." },
      { type: "h3", text: "Window 2: Days 11–30" },
      { type: "p", text: "If you haven't received serious offers by day 21, the market is telling you something. Adjust quickly — small, decisive price moves outperform slow, reluctant ones." },
      { type: "h3", text: "Window 3: Day 30+" },
      { type: "p", text: "Listings that linger past 30 days are perceived as either overpriced or flawed. Buyers ask their agents what's wrong with the property. Re-strategize completely before this happens." },
      { type: "quote", text: "Price is the most powerful marketing tool you have. Everything else — staging, photography, video — amplifies the price. It can't rescue it." },
      { type: "h2", text: "What I do differently" },
      { type: "p", text: "I run a full comparative market analysis using closed comps from the last 90 days, not the last 12 months. Older comps describe a market that no longer exists. We price for today, not yesterday." },
    ],
  },
  {
    slug: "first-time-buyer-myths-nj",
    image: princetonImg,
    title: "Five Myths Holding First-Time Buyers Back in New Jersey",
    excerpt:
      "From the 20% down payment myth to the 'wait for the crash' fantasy — here's what's actually true about buying your first NJ home.",
    category: "Buyer Insights",
    date: "2026-10-15",
    readMinutes: 6,
    body: [
      { type: "p", text: "First-time buyers in New Jersey carry more misinformation than any other group I work with. Most of it is well-intentioned advice from friends and family that was true a decade ago — and isn't anymore." },
      { type: "h2", text: "Myth 1: You need 20% down" },
      { type: "p", text: "FHA loans require 3.5%. Conventional loans go as low as 3%. NJHMFA offers down payment assistance up to $15,000 for qualified first-time buyers. Twenty percent is a preference, not a requirement." },
      { type: "h2", text: "Myth 2: Rates will crash next year" },
      { type: "p", text: "No serious economist is forecasting a rate crash. Modest declines, yes. A return to 3% mortgages — almost certainly not in this decade." },
      { type: "h2", text: "Myth 3: You'll save more by waiting" },
      { type: "p", text: "In Central NJ, prices have risen every year for the last decade except 2008–2010. Renting another year typically costs more than the rate savings you're waiting for." },
      { type: "h2", text: "Myth 4: Open houses are how you find homes" },
      { type: "p", text: "Most homes go under contract before the first open house. You need an agent watching the MLS in real time and writing offers within 48 hours of a listing going live." },
      { type: "h2", text: "Myth 5: A buyer's agent costs you money" },
      { type: "p", text: "In most NJ transactions, the seller's compensation structure covers the buyer's agent. You get expert representation, negotiation, and contract protection — typically at no out-of-pocket cost." },
    ],
  },
  {
    slug: "inventory-trends-shore-counties",
    image: oceanCountyImg,
    title: "Inventory Trends Along the Jersey Shore",
    excerpt:
      "Monmouth and Ocean counties are seeing the strongest inventory growth in three years. What it means for buyers, sellers, and second-home owners.",
    category: "Inventory & Pricing",
    date: "2026-10-02",
    readMinutes: 5,
    body: [
      { type: "p", text: "After three years of historically tight inventory, the Shore is finally taking a breath. Active listings in Monmouth and Ocean counties are up 18% and 24% year-over-year, respectively." },
      { type: "h2", text: "What's driving the shift" },
      { type: "ul", items: [
        "Sellers who were locked in by ultra-low pandemic-era rates are now re-entering the market.",
        "Investors who bought heavily in 2020–2022 are rebalancing portfolios.",
        "New construction in Ocean County is finally delivering after pandemic-era delays.",
      ]},
      { type: "h2", text: "What it means" },
      { type: "p", text: "Buyers — particularly in the $700K–$1.2M range — have meaningfully more selection. Sellers in this band need to lead with sharper pricing and stronger presentation. Coastal premium properties above $1.5M remain tightly supplied and competitive." },
    ],
  },
  {
    slug: "preparing-your-home-to-sell",
    image: njLuxuryInterior,
    title: "Preparing Your Home to Sell: A Pre-Listing Checklist",
    excerpt:
      "The 30-day pre-listing routine I walk every seller through — staging, repairs, and the small upgrades that consistently return more than they cost.",
    category: "Seller Insights",
    date: "2026-09-18",
    readMinutes: 8,
    body: [
      { type: "p", text: "The difference between a 'fine' listing and a great one is usually 30 days of focused preparation. Here's the checklist I use." },
      { type: "h2", text: "Weeks 1–2: De-personalize and de-clutter" },
      { type: "ul", items: [
        "Box up roughly one-third of your belongings, including 50% of closet contents.",
        "Remove family photos and personal collections — buyers need to imagine their life in the space.",
        "Deep-clean every surface, baseboard, and fixture. Cleanliness photographs.",
      ]},
      { type: "h2", text: "Weeks 2–3: Strategic repairs" },
      { type: "ul", items: [
        "Fresh, neutral paint in the most-photographed rooms (often the highest-ROI move).",
        "Replace dated light fixtures and any burned-out bulbs.",
        "Touch up scuffed trim, caulk, and grout.",
      ]},
      { type: "h2", text: "Week 4: Stage and photograph" },
      { type: "p", text: "Professional staging plus our drone and 3D video walkthrough package — this is where listings start to outperform. The investment routinely returns 3–5x on final sale price." },
    ],
  },
];

export const categories: InsightCategory[] = [
  "Market Update",
  "Buyer Insights",
  "Seller Insights",
  "Mortgage & Rates",
  "Inventory & Pricing",
  "Local Spotlight",
];

export const getArticleBySlug = (slug: string) =>
  articles.find((a) => a.slug === slug);

export const getRelatedArticles = (slug: string, limit = 3) =>
  articles
    .filter((a) => a.slug !== slug)
    .sort((a, b) => +new Date(b.date) - +new Date(a.date))
    .slice(0, limit);

export const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
